import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Shield, Moon } from 'lucide-react';
import CTA from '../../components/CTA';

const LarneChronicPain: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Chronic Pain Treatment Larne | Port Worker Back Pain Relief</title>
        <meta
          name="description"
          content="NHS-complementary chronic pain hypnotherapy serving Larne from Belfast clinic (21 miles). Neuroscience-based approach for fibromyalgia, back pain, shift work pain. 15 years nursing experience."
        />
        <meta name="keywords" content="chronic pain treatment Larne, fibromyalgia hypnotherapy Larne, pain management port town, shift work pain Larne, back pain treatment Larne" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/larne-chronic-pain/" />
        <meta property="og:title" content="Chronic Pain Treatment Larne | Port Worker Back Pain Relief" />
        <meta property="og:description" content="NHS-complementary chronic pain hypnotherapy serving Larne. Neuroscience-based approach for fibromyalgia, back pain, shift work pain." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Chronic Pain Treatment Larne",
            "description": "CNHC registered hypnotherapy for chronic pain management, serving Larne from Belfast clinic. Specializing in fibromyalgia, chronic back pain, arthritis, shift work-related pain with NHS-complementary approach.",
            "url": "https://www.sharonclare-hypnotherapy.com/larne-chronic-pain/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Chronic Pain Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Larne",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Antrim"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with chronic pain worsened by shift work?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is highly effective for chronic pain exacerbated by shift work. Many Larne residents work irregular port industry schedules, and disrupted sleep from shift work significantly worsens pain perception. Research shows sleep disruption amplifies central sensitization - your nervous system becomes hypersensitive when you're fatigued. Hypnotherapy works by calming the nervous system, improving sleep quality through relaxation techniques, and helping your brain develop healthier pain processing patterns. The approach addresses both the physical pain and the sleep disruption that makes it worse."
                }
              },
              {
                "@type": "Question",
                "name": "How does chronic pain hypnotherapy work alongside NHS care in Larne?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Northern Trust pain clinic waiting times often exceeding 12 months, hypnotherapy can provide relief while you wait for specialist appointments. I work collaboratively with your GP and medical team. With 15 years nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare providers. Many Larne clients use hypnotherapy alongside physiotherapy, medication, and other NHS treatments."
                }
              },
              {
                "@type": "Question",
                "name": "How far is Belfast from Larne for chronic pain treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Belfast is approximately 21 miles from Larne via the A8 coastal road, taking 30-35 minutes by car. This manageable distance makes regular pain management appointments practical for Larne residents. Many shift workers schedule appointments around their rotating schedules, and the flexibility of both in-person and online options accommodates irregular port industry hours."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer online chronic pain treatment for Larne shift workers?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Larne residents, which is ideal for shift workers with irregular schedules. Online sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions with maximum flexibility. You can schedule sessions during day shifts, night shifts, or rest days without the need to travel when you're fatigued from shift work. This flexibility is particularly valuable for port industry workers managing rotating patterns."
                }
              },
              {
                "@type": "Question",
                "name": "What chronic pain conditions do you treat for Larne clients?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "I work with Larne clients experiencing fibromyalgia, chronic back pain (particularly shift work and port industry-related), arthritis, migraines, neuropathic pain, repetitive strain injuries from physical labor, and pain worsened by sleep disruption. The neuroscience-based approach addresses the nervous system sensitization underlying these conditions, helping to reduce pain perception and improve sleep quality - both crucial for shift workers managing chronic pain."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions will I need for chronic pain management?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients see significant improvement within 6-8 sessions for chronic pain management, though some notice changes sooner. Complex pain conditions or long-standing pain may benefit from 8-10 sessions. We'll create a personalized treatment plan during your initial consultation based on your specific pain condition, how long you've had it, your shift work schedule, and your goals. The 4-session package provides excellent value while ensuring commitment to the process."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Treatment for Larne
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Neuroscience-based hypnotherapy serving Larne port town from Belfast
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">21 miles</span> from Larne via A8 • <span className="font-semibold">30-35 min drive</span>
                </p>
              </div>
              <p className="text-gray-600">Online sessions available - ideal for shift workers</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Chronic Pain Hypnotherapy for Larne
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                Serving Larne from our Belfast clinic, located approximately 21 miles southwest via the A8 coastal road. The 30-35 minute journey is straightforward and scenic, though many Larne shift workers choose online sessions for maximum flexibility around irregular port industry schedules. Both options provide the same professional, evidence-based chronic pain treatment.
              </p>
              <p>
                Living with chronic pain in Larne's port economy comes with unique challenges. Many residents work shift patterns at the ferry port or in industrial roles - rotating days, nights, and irregular hours that disrupt sleep. Research shows sleep disruption significantly worsens pain perception by amplifying nervous system sensitization. When you're fatigued from shift work, pain feels more intense, recovery is slower, and the cycle becomes relentless. Add the physical demands of port work - heavy lifting, repetitive movements, standing or sitting for long periods - and chronic pain becomes overwhelming.
              </p>
              <p>
                Modern pain neuroscience shows us that chronic pain is processed in the nervous system, not just the injury site. When we're sleep-deprived, fatigued from shift work, or stressed about irregular schedules, our nervous system becomes hypersensitive and amplifies pain signals. This is where solution-focused hypnotherapy excels. By calming your overactive alarm system, improving sleep quality through relaxation techniques, teaching your brain healthier pain processing patterns, and providing practical self-management tools, many clients experience significant reductions in pain levels.
              </p>
              <p>
                With 15 years of nursing experience at Belfast City Hospital, I understand both pain conditions and the impact of shift work on health. I've worked irregular hours myself, I know the exhaustion of night shifts, and I understand how difficult it is to manage chronic pain when your body clock is constantly disrupted. Hypnotherapy offers a science-backed approach that works alongside your medical care to help you regain control, reduce pain intensity, and improve quality of life despite challenging work schedules.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Chronic Pain */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              The Neuroscience of Chronic Pain
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Persistent pain lasting beyond 12 weeks isn't "all in your head," but it is in your nervous system. Understanding how pain works helps explain why hypnotherapy can be so effective.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Shield className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Central Sensitization</h3>
                <p className="text-gray-700">
                  The spinal cord and brain become hypersensitive, amplifying normal signals and interpreting them as pain. Sleep disruption from shift work significantly worsens this sensitization, making pain feel more intense when you're fatigued.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Fight-Flight-Freeze</h3>
                <p className="text-gray-700">
                  Ongoing stress from irregular schedules, physical labor demands, or fatigue keeps your survival system on high alert, which keeps pain signals turned up. Calming this system is essential for pain reduction.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Moon className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="font-heading font-semibold text-gray-900 text-xl mb-3">Sleep & Pain Connection</h3>
                <p className="text-gray-700">
                  Sleep disruption from shift work creates a vicious cycle - pain worsens sleep, poor sleep intensifies pain. Hypnotherapy improves sleep quality through relaxation techniques, breaking this cycle.
                </p>
              </div>
            </div>
            <div className="mt-8 bg-white rounded-2xl p-8">
              <p className="text-gray-700 text-lg">
                This is why you can feel real, distressing pain even when medical scans look "normal." The pain is genuine - your brain is simply protecting you a little too well, especially when sleep-deprived. Hypnotherapy works by calming this overactive alarm system and helping your brain interpret sensations differently.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              How Hypnotherapy Supports Pain Recovery
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8 text-center">
              Clinical hypnotherapy works directly with the mind-body connection to calm your overactive alarm system
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Heart className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Shift from Danger to Safety</h3>
                    <p className="text-gray-700">
                      Calms the fight-flight response so your nervous system stops amplifying pain signals. This is particularly important for shift workers dealing with sleep disruption, irregular schedules, and the physical demands of port industry work.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Brain className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Close the Pain Gate</h3>
                    <p className="text-gray-700">
                      Lowers the intensity of pain messages traveling through the spinal cord to the brain, reducing pain perception through descending pain inhibition pathways discovered in neuroscience research.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <Moon className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Improve Sleep Quality</h3>
                    <p className="text-gray-700">
                      Teaches deep relaxation and self-hypnosis techniques that improve sleep quality despite irregular shift patterns. Better sleep significantly reduces pain intensity and supports nervous system recovery.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <div className="flex items-start space-x-3 mb-4">
                  <CheckCircle className="w-8 h-8 text-accent-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading font-semibold text-gray-900 text-xl mb-2">Practical Self-Management Tools</h3>
                    <p className="text-gray-700">
                      Self-hypnosis and relaxation techniques you can use during shifts, before sleep, or at home to manage pain flare-ups and improve rest quality - crucial for shift workers.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <p className="text-gray-700 text-lg mb-4">
                Research supports hypnotherapy's role in multidisciplinary pain care. A major review of over 7,000 children with chronic abdominal pain found hypnotherapy 68% more effective than no treatment - outperforming dietary and medication-based approaches.
              </p>
              <p className="text-gray-700 text-lg">
                For Larne residents waiting months for Northern Trust pain clinic appointments, hypnotherapy offers evidence-based support that can provide relief while you wait for NHS specialist care, or work alongside the treatment you're already receiving.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NHS-Complementary Positioning */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6 text-center">
              Working Alongside Your Medical Care
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Northern Trust pain clinic waiting times often exceeding 12 months, many Larne residents need support now while waiting for specialist appointments. Hypnotherapy can provide that bridge.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                I work collaboratively with your GP, pain specialists, physiotherapists, and other healthcare providers. Many chronic pain clinics now recommend hypnotherapy alongside physiotherapy, medication, mindfulness, or gentle movement programs. By addressing the nervous system directly, hypnotherapy enhances these treatments and helps you regain confidence, mobility, and quality of life.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                With 15 years of nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare team. I know the medications, the procedures, the specialist terminology, and the frustrations of chronic pain from both sides of the clinical encounter. I also understand the unique health challenges of shift work.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Medical Background</h3>
                <p className="text-gray-700">15 years Belfast City Hospital nursing experience brings clinical credibility to pain management</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">CNHC Registered</h3>
                <p className="text-gray-700">Professional registration ensures you receive evidence-based, ethical treatment</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">GP Collaboration</h3>
                <p className="text-gray-700">I work with your GP and medical team, not in competition with them</p>
              </div>
              <div className="bg-white rounded-xl p-6">
                <CheckCircle className="w-8 h-8 text-accent-600 mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Evidence-Based Approach</h3>
                <p className="text-gray-700">Neuroscience-backed techniques supported by research, not pseudoscience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conditions Treated */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Chronic Pain Conditions We Address
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Fibromyalgia</h3>
                <p className="text-gray-700">Widespread pain, fatigue, and sensitivity worsened by sleep disruption. Hypnotherapy addresses the nervous system sensitization and sleep quality - crucial for shift workers.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Chronic Back Pain</h3>
                <p className="text-gray-700">Lower back pain, sciatica from port industry work - heavy lifting, repetitive movements, standing. Calming stress and improving sleep significantly reduces back pain intensity.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Arthritis Pain</h3>
                <p className="text-gray-700">Osteoarthritis and rheumatoid arthritis pain management. Hypnotherapy helps reduce pain perception, improve mobility, and maintain work capacity despite demanding shifts.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Migraines & Headaches</h3>
                <p className="text-gray-700">Chronic migraine, tension headaches often triggered by irregular sleep patterns. Stress reduction and sleep improvement through hypnotherapy can reduce frequency and intensity.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Shift Work Pain Syndrome</h3>
                <p className="text-gray-700">Pain intensified by sleep disruption and circadian rhythm disturbance. Hypnotherapy improves sleep quality and reduces pain amplification from fatigue.</p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Repetitive Strain Injuries</h3>
                <p className="text-gray-700">RSI, carpal tunnel, work-related pain from port industry labor. Hypnotherapy reduces pain amplification and muscle tension effectively.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Larne Clients Choose Sharon Clare for Pain Management
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  15 Years Nursing Experience
                </h3>
                <p className="text-gray-700">
                  With 15 years at Belfast City Hospital working irregular shifts myself, I understand pain conditions from a medical perspective and the unique challenges of shift work. I've experienced the exhaustion of night shifts, the difficulty of maintaining health on irregular schedules, and how to manage it effectively. This medical background brings credibility and understanding to shift worker pain management.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Understanding Shift Work Pain
                </h3>
                <p className="text-gray-700">
                  I understand the unique pain patterns facing Larne's port industry workers - the sleep disruption from rotating shifts, physical demands of port work, the exhaustion that amplifies pain. The solution-focused approach addresses both the physical pain and the sleep/circadian rhythm disruption that makes it worse.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Convenient A8 Access
                </h3>
                <p className="text-gray-700">
                  The Belfast clinic is easily accessible via the A8 coastal road from Larne - just 30-35 minutes. Many shift workers schedule appointments around their rotating patterns, making pain management fit with irregular schedules. The scenic coastal drive can be therapeutic.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Online Sessions for Shift Workers
                </h3>
                <p className="text-gray-700">
                  Secure online sessions via video call provide maximum flexibility for shift workers with irregular schedules. Schedule appointments during day shifts, night shifts, or rest days without traveling when you're fatigued. This flexibility is ideal for port industry workers managing rotating patterns and chronic pain simultaneously.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Sleep Quality Improvement
                </h3>
                <p className="text-gray-700">
                  Specialized focus on improving sleep quality despite irregular shift patterns - crucial for pain management. Self-hypnosis techniques help you rest better between shifts, reducing pain intensity and supporting recovery. Better sleep breaks the pain-fatigue cycle.
                </p>
              </div>
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Evidence-Based Neuroscience Approach
                </h3>
                <p className="text-gray-700">
                  Solution-focused hypnotherapy combines the latest pain neuroscience research with proven therapeutic techniques. Rather than dwelling on pain history, we focus on solutions, improving sleep, building resilience, and practical strategies for managing pain while working demanding shifts.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting There */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Larne
            </h2>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the A8 southwest from Larne, join the M2 into Belfast, then head south to the Ormeau Road
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> Approximately 21 miles
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> 30-35 minutes in normal traffic
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">A Straightforward Route</h3>
                <p className="text-gray-700">
                  The A8 and M2 provide a straightforward run from Larne into Belfast, with the Ormeau Road just south of the city centre. Many shift workers schedule appointments around their rotating patterns, making pain management appointments fit with irregular schedules. The drive itself can be therapeutic and relaxing.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions for Maximum Flexibility</h3>
              <p className="text-gray-700">
                If traveling is difficult due to your pain condition or shift work schedule, I offer secure online hypnotherapy sessions via video call. Online sessions provide the same professional treatment with the convenience of attending from your home in Larne. This option is ideal for shift workers with irregular hours, those experiencing pain flare-ups, or anyone who prefers online therapy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Larne Area Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working shift patterns at the port with chronic back pain was unbearable. The online sessions with Sharon were perfect - I could schedule around my shifts without traveling when I was exhausted. Her hypnotherapy helped me understand how lack of sleep was making my pain worse. After 8 sessions, my pain is manageable and I'm sleeping better despite the shifts. Life-changing."
                </p>
                <p className="font-semibold text-gray-900">D.K., Larne - Chronic Back Pain</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Living with fibromyalgia while working night shifts was impossible. Sharon understood both the pain and the shift work challenges from her nursing background. The relaxation techniques helped me sleep better between shifts, and the pain levels dropped significantly. I can manage work now without constant suffering. Her flexibility with online sessions was essential."
                </p>
                <p className="font-semibold text-gray-900">M.T., Larne - Fibromyalgia</p>
              </div>
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Chronic migraines triggered by irregular shift patterns were controlling my life. Sharon's hypnotherapy approach addressed the sleep disruption and stress that medication couldn't reach. The online sessions fit perfectly with my rotating schedule. The frequency of my migraines has reduced dramatically. I feel like I have control again instead of pain controlling me."
                </p>
                <p className="font-semibold text-gray-900">L.H., Larne - Chronic Migraines</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Larne Chronic Pain Clients
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with chronic pain worsened by shift work?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is highly effective for chronic pain exacerbated by shift work. Many Larne residents work irregular port industry schedules, and disrupted sleep from shift work significantly worsens pain perception. Research shows sleep disruption amplifies central sensitization - your nervous system becomes hypersensitive when you're fatigued. Hypnotherapy works by calming the nervous system, improving sleep quality through relaxation techniques, and helping your brain develop healthier pain processing patterns. The approach addresses both the physical pain and the sleep disruption that makes it worse.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How does chronic pain hypnotherapy work alongside NHS care in Larne?
                </h3>
                <p className="text-gray-700">
                  Hypnotherapy is complementary to NHS care, not a replacement for medical treatment. With Northern Trust pain clinic waiting times often exceeding 12 months, hypnotherapy can provide relief while you wait for specialist appointments. I work collaboratively with your GP and medical team. With 15 years nursing experience at Belfast City Hospital, I understand pain conditions from a medical perspective and can communicate effectively with your healthcare providers. Many Larne clients use hypnotherapy alongside physiotherapy, medication, and other NHS treatments for comprehensive pain management.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Belfast from Larne for chronic pain treatment?
                </h3>
                <p className="text-gray-700">
                  Belfast is approximately 21 miles from Larne via the A8 coastal road, taking 30-35 minutes by car. This manageable distance makes regular pain management appointments practical for Larne residents. Many shift workers schedule appointments around their rotating schedules, and the flexibility of both in-person and online options accommodates irregular port industry hours. The scenic coastal drive can be therapeutic and helps create a transition into healing mindset.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you offer online chronic pain treatment for Larne shift workers?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. I offer secure online hypnotherapy sessions via video call for Larne residents, which is ideal for shift workers with irregular schedules. Online sessions provide the same professional, evidence-based chronic pain treatment as in-person sessions with maximum flexibility. You can schedule sessions during day shifts, night shifts, or rest days without the need to travel when you're fatigued from shift work. This flexibility is particularly valuable for port industry workers managing rotating patterns and chronic pain simultaneously.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  What chronic pain conditions do you treat for Larne clients?
                </h3>
                <p className="text-gray-700">
                  I work with Larne clients experiencing fibromyalgia, chronic back pain (particularly shift work and port industry-related), arthritis, migraines triggered by irregular sleep, neuropathic pain, repetitive strain injuries from physical labor, and pain worsened by sleep disruption. The neuroscience-based approach addresses the nervous system sensitization underlying these conditions, helping to reduce pain perception and improve sleep quality - both crucial for shift workers managing chronic pain while maintaining demanding work schedules.
                </p>
              </div>
              <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I need for chronic pain management?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvement within 6-8 sessions for chronic pain management, though some notice changes sooner. Complex pain conditions or long-standing pain may benefit from 8-10 sessions. We'll create a personalized treatment plan during your initial consultation based on your specific pain condition, how long you've had it, your shift work schedule, and your goals. The 4-session package provides excellent value while ensuring commitment to the process and allowing time for meaningful improvements in both pain levels and sleep quality.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Take the First Step Toward Pain Relief"
        subtitle="If you're ready to explore how clinical hypnotherapy can help with chronic pain and shift work challenges, book your initial consultation today and start retraining your brain to turn down the volume on pain."
        primaryButtonText="Request 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default LarneChronicPain;
